// Vendor libraries
import { ChangeDetectorRef, EventEmitter, SimpleChanges, } from '@angular/core';
// Components
import { BaseAutocompleteComponent } from '../base-autocomplete/components/base-autocomplete.component';
import { ConfirmModalComponent } from 'NewAngular/features/components/modals/confirm-modal/confirm-modal.component';
import * as i0 from "@angular/core";
const _c0 = ["autocompleteComponent"];
/**
 *
 * Autocomplete In Place Directive
 *
 * This is an abstract base class that handles most of the logic for the Autocomplete In Place Components.
 * It contains an abstract method, `setDisplayIdentifier`, which is implemented differently by the
 * `SecurityAutocompleteInPlaceComponent` and the `WatchlistAutocompleteInPlaceComponent`
 *
 * There is no need to declare an abstract class as part of a module.
 *
 * Input Properties:
 *   appendToBody: Whether to append the results to the DOM body or not. This is used when the autocompleter
 *                 needs to visually appear outside the confines component's bounding box.
 *   displayIdentifier: The identifier used to fetch data about the selected item from the backend.
 *   hideCancelButton: A boolean that determines whether the cancel button is hidden.
 *   searchType: Which autocompleter results should be fetched from.
 *   searchBlockRegex: Enable raw inputs that match the regex.
 *   tooltipText: The tooltip to display on hovering the edit icon.
 *   showRemove: Whether to show the remove icon.
 *   errorMessage: error message for current security
 *   disableEditBtn: disable security edit button
 *   isDisabled: to disable the autocompleter input
 *
 * Output Properties:
 *   editHandler: EventEmitter that emits whenever the edit button is selected.
 *   removeHandle: Emits when the remove button is clicked.
 *   submitHandler: EventEmitter that emits the selected AutocompleteResult.
 *
 */
export class AutocompleteInPlaceDirective {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.buttonType = '';
        /**
         * The display identifier is:
         *  securities: string ex: 'P:123456'
         *  watchlists: number or null
         *  metrics: string, ex: 'market_alpha_12_month'
         */
        this.displayIdentifier = '';
        this.placeholder = 'Search';
        this.dropdownMenuClass = '';
        this.errorMessage = '';
        this.disableEditBtn = false;
        this.disableAutocompleterInput = false;
        // TODO: Add types for other autocomplete search types
        this.searchType = '';
        this.editHandler = new EventEmitter();
        this.removeHandler = new EventEmitter();
        this.submitHandler = new EventEmitter();
        this.confirmMessage = '';
        this.displayValue = '';
        this.isEditing = true;
    }
    ngOnChanges(changes) {
        if (changes?.displayIdentifier) {
            this.setDisplayValue();
        }
    }
    edit() {
        this.isEditing = true;
        // detect changes so that we immediately show the input element and hide the edit button
        this.changeDetector.detectChanges();
        // set focus on the input element
        this.autocompleteComponent.focus();
        this.editHandler.emit();
    }
    cancel() {
        this.isEditing = false;
    }
    reset() {
        this.displayValue = '';
        this.isEditing = true;
    }
    remove() {
        this.reset();
        this.removeHandler.emit();
    }
    blurHandler() {
        /**
         * If there is a value to display, display the value; otherwise, continue showing the search bar. By default,
         * this behavior works for autocomplete in place instances that require a selection to be made.
         *
         * Some autocomplete in place instances require a selection to be made, e.g. scatter plot metrics or custom
         * security benchmark. In those cases, we want to show the previously selected result on blur
         *
         * However, some autocomplete in places are optional, e.g. MP list page benchmark / watchlist filter. In such
         * cases, the `editHandler` should reset the `displayIdentifier` to a null / empty value, which will allow for
         * proper resetting of the autocompleter.
         */
        if (!this.displayIdentifier)
            return;
        // setTimeout prevents NG0100 error, by handling blur until after Angular current change-detection cycle
        setTimeout(() => {
            this.cancel();
            this.changeDetector.markForCheck();
        });
    }
    confirmCallback(confirmed) {
        /**
         * Called when the user chooses an option in the confirm modal. If they click continue, then proceed
         * with that object as usual. If they click cancel, leave the component in isEditing mode and do not
         * call the submit handler
         */
        if (confirmed) {
            this.displayAndHandleObject(this.object);
        }
    }
    displayAndHandleObject(object) {
        /**
         * If an object was selected, display it to the user and call the submit handler on it.
         */
        if (object !== undefined) {
            this.displayValue = object.obj_name || object.name;
            this.isEditing = false;
            this.submitHandler.emit(object);
        }
    }
    submit(object) {
        this.displayAndHandleObject(object);
    }
}
AutocompleteInPlaceDirective.ɵfac = function AutocompleteInPlaceDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AutocompleteInPlaceDirective)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
AutocompleteInPlaceDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: AutocompleteInPlaceDirective, viewQuery: function AutocompleteInPlaceDirective_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
        i0.ɵɵviewQuery(ConfirmModalComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.autocompleteComponent = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.confirmModal = _t.first);
    } }, inputs: { appendToBody: "appendToBody", buttonType: "buttonType", displayIdentifier: "displayIdentifier", hideCancelButton: "hideCancelButton", placeholder: "placeholder", dropdownMenuClass: "dropdownMenuClass", errorMessage: "errorMessage", disableEditBtn: "disableEditBtn", disableAutocompleterInput: "disableAutocompleterInput", searchType: "searchType", searchBlockRegex: "searchBlockRegex", tooltipText: "tooltipText", showRemove: "showRemove" }, outputs: { editHandler: "editHandler", removeHandler: "removeHandler", submitHandler: "submitHandler" }, features: [i0.ɵɵNgOnChangesFeature] });
